/**
 * WellsTab — Phase 24 / Task 24.11 (DEC-104) + follow-ups (DEC-107, DEC-108).
 *
 * The fourth "Sites & Operators" tab. A [My Catalog | Browse All] segmented
 * toggle splits two views (DEC-108):
 *   - My Catalog (default): only wells already in this hauler's catalog
 *     (hauler_wells). Loads with no query; operator filter + text search narrow it.
 *   - Browse All: the full lodeX Pro wells catalog (server search, 2-char minimum,
 *     OR pick an operator to browse all its wells) — the mode for finding new wells.
 * Both modes badge already-imported wells. Clicking a result opens the inline well
 * detail; the mode + operator filter persist across that navigation.
 */
import * as React from 'react';
import {
  useWellSearch,
  useHaulerOperators,
  useOperatorWells,
  type WellResult,
  type OperatorWell,
} from './hooks';
import { rowStyle, subText, searchInputStyle, Pill, AddButton, SectionHeader } from './ui-bits';
import { fieldStyle, labelStyle } from './form-bits';
import { WellDetail } from './WellDetail';
import { BulkImportWellsModal } from './BulkImportWellsModal';
import { formatNumber } from '@lodex-pro/shared/number-format';

type Mode = 'my' | 'all';

/** Normalized row shape rendered identically for search + browse results. */
interface DisplayWell {
  readonly id: string;
  readonly wellName: string;
  readonly apiNumber: string;
  readonly operatorName: string | null;
  readonly county: string | null;
  readonly state: string;
  readonly wellType: string | null;
  readonly inHaulerCatalog: boolean;
  readonly tankLabel: string | null;
}

/** Debounce a fast-changing value (search box) to throttle server calls. */
function useDebounced<T>(value: T, ms: number): T {
  const [debounced, setDebounced] = React.useState(value);
  React.useEffect(() => {
    const t = setTimeout(() => setDebounced(value), ms);
    return (): void => clearTimeout(t);
  }, [value, ms]);
  return debounced;
}

function fromSearch(w: WellResult): DisplayWell {
  return {
    id: w.id,
    wellName: w.wellName,
    apiNumber: w.apiNumber,
    operatorName: w.operatorName,
    county: w.county,
    state: w.state,
    wellType: w.wellType,
    inHaulerCatalog: w.inHaulerCatalog,
    tankLabel:
      w.tankCount > 0 ? `${formatNumber(w.tankCount)} tank${w.tankCount === 1 ? '' : 's'}` : null,
  };
}

function fromBrowse(w: OperatorWell, operatorName: string | null): DisplayWell {
  return {
    id: w.id,
    wellName: w.wellName,
    apiNumber: w.apiNumber,
    operatorName,
    county: w.county,
    state: w.state,
    wellType: w.wellType,
    inHaulerCatalog: w.hasHaulerWells,
    tankLabel: w.hasHaulerTanks ? 'Has tanks' : null,
  };
}

function Segmented({
  value,
  onChange,
}: {
  value: Mode;
  onChange: (v: Mode) => void;
}): React.JSX.Element {
  const seg = (v: Mode, label: string): React.JSX.Element => (
    <button
      type="button"
      onClick={(): void => onChange(v)}
      aria-pressed={value === v}
      style={{
        padding: '6px 16px',
        fontSize: '13px',
        fontWeight: 600,
        cursor: 'pointer',
        border: 'none',
        backgroundColor: value === v ? 'var(--color-accent-blue)' : 'transparent',
        color: value === v ? 'var(--color-on-accent)' : 'var(--color-text-secondary)',
      }}
    >
      {label}
    </button>
  );
  return (
    <div
      style={{
        display: 'inline-flex',
        border: '1px solid var(--color-border-secondary)',
        borderRadius: '6px',
        overflow: 'hidden',
        marginBottom: '12px',
      }}
    >
      {seg('my', 'My Catalog')}
      {seg('all', 'Browse All')}
    </div>
  );
}

export function WellsTab({ canManage }: { canManage: boolean }): React.JSX.Element {
  const [mode, setMode] = React.useState<Mode>('my');
  const [query, setQuery] = React.useState('');
  const [operatorFilter, setOperatorFilter] = React.useState(''); // '' = all operators
  const [selectedId, setSelectedId] = React.useState<string | null>(null);
  const [importOpen, setImportOpen] = React.useState(false);
  const debounced = useDebounced(query.trim(), 250);
  const onlyMyCatalog = mode === 'my';

  const { data: opsData } = useHaulerOperators();
  const operators = (opsData?.haulerOperators ?? []).filter(
    (o) => o.isActive && o.operatorId !== null,
  );

  const browsing = operatorFilter !== '';
  const search = useWellSearch(browsing ? '' : debounced, undefined, onlyMyCatalog);
  const browse = useOperatorWells(
    browsing ? operatorFilter : '',
    undefined,
    undefined,
    onlyMyCatalog,
  );

  if (selectedId !== null) {
    return (
      <WellDetail
        wellId={selectedId}
        canManage={canManage}
        onClose={(): void => setSelectedId(null)}
      />
    );
  }

  // "My Catalog" lists without a query; a full-catalog (Browse All) search needs 2+ chars.
  const tooShortSearch = !browsing && mode === 'all' && debounced.length < 2;
  const hasFilter = browsing || debounced.length > 0;
  let rows: DisplayWell[] = [];
  let loading = false;
  let error = false;

  if (browsing) {
    loading = browse.isFetching;
    error = browse.isError;
    const opName = operators.find((o) => o.operatorId === operatorFilter)?.resolvedName ?? null;
    const q = debounced.toLowerCase();
    rows = (browse.data?.wells ?? [])
      .filter(
        (w) =>
          q === '' ||
          w.wellName.toLowerCase().includes(q) ||
          w.apiNumber.toLowerCase().includes(q) ||
          (w.county ?? '').toLowerCase().includes(q),
      )
      .map((w) => fromBrowse(w, opName));
  } else if (!tooShortSearch) {
    loading = search.isFetching;
    error = search.isError;
    rows = (search.data?.wells ?? []).map(fromSearch);
  }

  const emptyVirginCatalog = mode === 'my' && !hasFilter && !loading && !error && rows.length === 0;

  return (
    <div>
      <SectionHeader
        title="Wells"
        action={
          canManage ? (
            <AddButton label="Import Wells" onClick={(): void => setImportOpen(true)} />
          ) : null
        }
      />

      <Segmented value={mode} onChange={setMode} />

      <label htmlFor="wells-operator-filter" style={labelStyle}>
        Filter by operator
      </label>
      <select
        id="wells-operator-filter"
        value={operatorFilter}
        onChange={(e): void => setOperatorFilter(e.target.value)}
        style={{ ...fieldStyle, marginBottom: '10px' }}
      >
        <option value="">All operators</option>
        {operators.map((o) => (
          <option key={o.id} value={o.operatorId ?? ''}>
            {o.resolvedName} ({o.ticketAbbreviation})
          </option>
        ))}
      </select>

      <input
        type="search"
        value={query}
        onChange={(e): void => setQuery(e.target.value)}
        placeholder={
          mode === 'my' ? 'Filter your wells by name or API' : 'Search wells by name or operator'
        }
        aria-label="Search or filter wells"
        style={searchInputStyle}
      />

      {emptyVirginCatalog && (
        <p style={subText}>
          You haven&apos;t added any wells yet. Switch to Browse All to find and import wells.
        </p>
      )}
      {tooShortSearch && <p style={subText}>Search for wells or select an operator to browse.</p>}
      {!tooShortSearch && loading && <p style={subText}>{browsing ? 'Loading…' : 'Searching…'}</p>}
      {!tooShortSearch && error && (
        <p style={{ ...subText, color: 'var(--color-danger)' }}>Could not load wells.</p>
      )}
      {!tooShortSearch && !loading && !error && rows.length === 0 && !emptyVirginCatalog && (
        <p style={subText}>
          {mode === 'my' ? 'No wells in your catalog match.' : `No wells match “${debounced}”.`}
        </p>
      )}

      {rows.map((w) => (
        <WellRow key={w.id} well={w} onOpen={(): void => setSelectedId(w.id)} />
      ))}

      {canManage && <BulkImportWellsModal open={importOpen} onOpenChange={setImportOpen} />}
    </div>
  );
}

function WellRow({ well, onOpen }: { well: DisplayWell; onOpen: () => void }): React.JSX.Element {
  const place = [well.county, well.state].filter((s) => s != null && s !== '').join(', ');
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e): void => {
        if (e.key === 'Enter' || e.key === ' ') onOpen();
      }}
      style={{ ...rowStyle, cursor: 'pointer' }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 600, color: 'var(--color-text-primary)' }}>
          {well.wellName} <span style={{ ...subText, fontWeight: 400 }}>API {well.apiNumber}</span>
        </div>
        <div style={subText}>
          {well.operatorName ?? 'Unknown operator'}
          {place !== '' ? ` · ${place}` : ''}
          {well.wellType != null ? ` · ${well.wellType}` : ''}
        </div>
      </div>
      {well.inHaulerCatalog && <Pill label="In your catalog" tone="positive" />}
      {well.tankLabel != null && <Pill label={well.tankLabel} tone="accent" />}
    </div>
  );
}
